import Icon from '@/components/public/ui/Icon';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
  className?: string;
}

function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | '...')[] = [1];
  if (current > 3) pages.push('...');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

const base =
  'flex h-10 w-10 items-center justify-center rounded-md border text-sm font-medium transition-colors duration-200';
const idle = 'border-line bg-surface text-on-surface hover:border-primary hover:text-primary';
const disabled = 'border-line bg-surface text-on-surface-light/40 cursor-not-allowed';

export default function Pagination({
  currentPage,
  totalPages,
  onChange,
  className = '',
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const pages = getPageNumbers(currentPage, totalPages);

  return (
    <div className={`flex items-center justify-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={() => onChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Página anterior"
        className={`${base} ${currentPage === 1 ? disabled : idle}`}
      >
        <Icon name="chevron_left" size="sm" />
      </button>

      {pages.map((page, i) =>
        page === '...' ? (
          <span
            key={`ellipsis-${i}`}
            className="text-on-surface-light flex h-10 w-10 items-center justify-center"
          >
            …
          </span>
        ) : (
          <button
            key={page}
            type="button"
            onClick={() => onChange(page)}
            aria-current={currentPage === page ? 'page' : undefined}
            className={`${base} ${
              currentPage === page ? 'border-primary bg-primary text-white' : idle
            }`}
          >
            {page}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => onChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Página seguinte"
        className={`${base} ${currentPage === totalPages ? disabled : idle}`}
      >
        <Icon name="chevron_right" size="sm" />
      </button>
    </div>
  );
}
